import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { CowreMark, IconClose } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { stagesSum, embryosCreated, discardedCount, preservationDirective, STAGES } from '../../../core/constants/clivage';
import { useOpuSession } from '../../opu/contexts/opu';
import { useLabsList } from '../../labs/contexts/labs';
import SemenSearch from '../../semen/forms/search';
import { useSessionClivages } from '../contexts/clivages';
import { isEmptyRow } from '../utils/validate';
import { clivagePaths } from '../queries/paths';
import { clivageKeys } from '../queries/keys';
import ClivageEditRows from '../forms/clivage-edit-rows';
import ReorganizeModal from '../forms/reorganize-modal';

import './styles/workspace.css';

const today = () => new Date().toLocaleDateString('en-CA');
// A row with no clivage yet is "empty" (skipped on save, never blocks) until the
// user enters the data that defines a fertilization — oocytes or stages. Reuses
// the create path's predicate (isEmptyRow) so both grids agree on "empty"; semen
// alone does NOT make a row count, so picking it can't trap the user into a
// blocked save. Applies whether the donor is pre-bound (seeded from an aspiration)
// or still to be picked (manually added).
const isEmptyNew = (r) => !r.clivageId && isEmptyRow(r);
// canonical (order- and zero-independent) stages object → for diffing + payload
const stagesObj = (stages) => {
    const o = {};
    STAGES.forEach(s => { const n = Number(stages?.[s.value]) || 0; if (n > 0) o[s.value] = n; });
    return o;
};

export default function ClivageEditWorkspace() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [params] = useSearchParams();
    const sessionId = params.get('session');

    const { data: session, isLoading, error } = useOpuSession(sessionId, 'resolution');
    const aspirations = (session?.aspirations || []).filter(a => a.status !== 0);
    const aspirationIds = aspirations.map(a => a.id);
    const { data: clivages, isLoading: loadingClivages, isError: clivagesError } = useSessionClivages(sessionId, aspirationIds);

    const { data: labsData } = useLabsList({ limit: 100 });
    const labs = labsData?.items ?? [];

    const donors = aspirations.map(a => ({ aspiration_id: a.id, name: a.donor?.name || '—', number: a.donor?.number || '', viables: a.viables ?? 0 }));
    const donorById = Object.fromEntries(donors.map(d => [String(d.aspiration_id), d]));

    const [rows, setRows] = useState(null);
    const [loaded, setLoaded] = useState(null); // frozen snapshot of clivages at seed (DELETE diff source)
    const [errors, setErrors] = useState({});
    const [labId, setLabId] = useState('');
    const [labDirty, setLabDirty] = useState(false);
    const [date, setDate] = useState(today());
    const [dateDirty, setDateDirty] = useState(false);
    const [sameSemen, setSameSemen] = useState(false);
    const [globalSemen, setGlobalSemen] = useState(null);
    const [allSexed, setAllSexedState] = useState(false);
    const [seq, setSeq] = useState(1);
    const [saving, setSaving] = useState(false);
    const [seeded, setSeeded] = useState(false);
    const [showReorg, setShowReorg] = useState(false);

    if (session && clivages && !seeded) {
        setLoaded(clivages);
        const clivageRows = clivages.map(c => ({
            key: `c${c.id}`,
            clivageId: c.id,
            aspiration_id: c.aspiration_id,
            donorName: c.aspiration?.donor?.name || '—',
            donorNumber: c.aspiration?.donor?.number || '',
            viables: c.aspiration?.viables ?? 0,
            semen: c.semen ? { id: c.semen.id, code: c.semen.code, name: c.semen.name } : null,
            oocytes: String(c.oocytes ?? ''),
            stages: { ...(c.stages || {}) },
            sexed: c.type === 'sexado',
            sexedTouched: false,
            preservation: 'fresh',
            embryosFloor: (c.embryos || []).filter(em => em.status !== 0).length, // matches the 422 guard exactly
            orig: { semen_id: c.semen_id, lab_id: c.lab_id, date: c.date, oocytes: c.oocytes, stages: c.stages || {}, type: c.type || null },
        }));
        // Aspirations added to the session after the clivage step have no clivage
        // record yet — seed an empty (all-zero) row per one so the user can clivage
        // it here, or just leave/remove it. Donor is fixed; empty rows are skipped
        // on save (isEmptyNew) so they never block.
        const clivagedAsp = new Set(clivages.map(c => c.aspiration_id));
        const extraRows = aspirations.filter(a => !clivagedAsp.has(a.id)).map(a => ({
            key: `a${a.id}`,
            clivageId: null,
            fromAspiration: true,
            aspiration_id: a.id,
            donorName: a.donor?.name || '—',
            donorNumber: a.donor?.number || '',
            viables: a.viables ?? 0,
            semen: null,
            oocytes: '',
            stages: {},
            sexed: false,
            sexedTouched: false,
            preservation: 'fresh',
            embryosFloor: 0,
        }));
        setRows([...clivageRows, ...extraRows]);
        setLabId(clivages[0]?.lab_id ? String(clivages[0].lab_id) : '');
        setDate(clivages[0]?.date || session.date || today());
        setSeeded(true);
    }

    if (clivagesError) return <div className="cl-ws__state">No se pudieron cargar los clivages. Recargá la página.</div>;
    if (isLoading || loadingClivages || (!session && !error)) return <div className="cl-ws__state">Cargando clivages…</div>;
    if (error || !session) return <div className="cl-ws__state">Sesión OPU no encontrada.</div>;

    const clearErr = (key, field) => setErrors(prev => (prev[key] ? { ...prev, [key]: { ...prev[key], [field]: undefined } } : prev));

    const setField = (key, field, value) => {
        if (field === 'oocytes' && value !== '') {
            const row = (rows || []).find(r => r.key === key);
            if (row && row.viables != null && Number(value) > row.viables) { toast.error(`Óvocitos no pueden superar los viables (${row.viables})`); return; }
        }
        setRows(prev => prev.map(r => {
            if (r.key !== key) return r;
            if (field === 'aspiration_id') {
                const d = donorById[value];
                return { ...r, aspiration_id: value, donorName: d?.name || '', donorNumber: d?.number || '', viables: d?.viables ?? 0 };
            }
            return { ...r, [field]: value };
        }));
        clearErr(key, field === 'aspiration_id' ? 'donor' : field);
    };
    const setStage = (key, stage, value) => {
        if (value !== '') {
            const row = (rows || []).find(r => r.key === key);
            const nextEmb = stagesSum({ ...row?.stages, [stage]: value });
            if (row && row.oocytes !== '' && Number.isFinite(Number(row.oocytes)) && nextEmb > Number(row.oocytes)) {
                toast.error(`Los embriones no pueden superar los óvocitos (${row.oocytes})`); return;
            }
        }
        setRows(prev => prev.map(r => (r.key === key ? { ...r, stages: { ...r.stages, [stage]: value } } : r)));
        clearErr(key, 'embryos');
    };
    const setSexed = (key, value) => setRows(prev => prev.map(r => (r.key === key ? { ...r, sexed: value, sexedTouched: true } : r)));
    const setSemen = (key, semen) => { setRows(prev => prev.map(r => (r.key === key ? { ...r, semen } : r))); clearErr(key, 'semen'); };
    const clearSemen = (key) => setRows(prev => prev.map(r => (r.key === key ? { ...r, semen: null } : r)));
    // Otro clivage para una donadora concreta (append a nivel de donadora): se inserta
    // debajo del grupo de esa donadora; donante fijo (sin selector). Hereda "todo sexado".
    const addClivage = (aspirationId) => {
        const d = donors.find(x => String(x.aspiration_id) === String(aspirationId));
        if (!d) return;
        setRows(prev => {
            const newRow = { key: `n${seq}`, clivageId: null, aspiration_id: d.aspiration_id, donorName: d.name, donorNumber: d.number, viables: d.viables, semen: null, oocytes: '', stages: {}, sexed: allSexed, sexedTouched: true, preservation: 'fresh', embryosFloor: 0 };
            let lastIdx = -1;
            prev.forEach((r, i) => { if (String(r.aspiration_id) === String(aspirationId)) lastIdx = i; });
            const next = [...prev];
            next.splice(lastIdx === -1 ? next.length : lastIdx + 1, 0, newRow); // donadora sin filas → al final, no al frente
            return next;
        });
        setSeq(s => s + 1);
    };
    const removeRow = (key) => setRows(prev => prev.filter(r => r.key !== key));

    const toggleAllSexed = (v) => { setAllSexedState(v); setRows(prev => prev.map(r => ({ ...r, sexed: v, sexedTouched: true }))); };
    // Reorganiza por DONADORA (grupo): mantiene juntos sus clivages.
    const applyOrder = (ids) => {
        setRows(prev => {
            const byAsp = new Map();
            prev.forEach(r => { const k = String(r.aspiration_id); if (!byAsp.has(k)) byAsp.set(k, []); byAsp.get(k).push(r); });
            return ids.flatMap(id => byAsp.get(String(id)) || []);
        });
        setShowReorg(false);
    };

    const list = rows || [];
    const effSemen = (r) => (sameSemen ? globalSemen : r.semen);
    // Recálculo de ÓV al enviar: filas con intención de fertilizar (semen o embriones)
    // y la casilla de óvocitos VACÍA se rellenan con los viables restantes de su
    // donadora (repartidos si hay varias); un 0 se deja en blanco (fila vacía → se omite).
    const recalcOocytes = (current) => {
        const byAsp = new Map();
        current.forEach(r => { const k = String(r.aspiration_id); if (!byAsp.has(k)) byAsp.set(k, []); byAsp.get(k).push(r); });
        const fill = new Map();
        byAsp.forEach((group) => {
            const intent = (r) => (!!effSemen(r) || stagesSum(r.stages) > 0);
            const blanks = group.filter(r => intent(r) && r.oocytes === '');
            if (!blanks.length) return;
            const viables = group[0]?.viables ?? 0;
            const filled = group.filter(r => r.oocytes !== '').reduce((a, r) => a + (Number(r.oocytes) || 0), 0);
            const remaining = Math.max(0, viables - filled);
            const base = Math.floor(remaining / blanks.length);
            blanks.forEach((r, i) => {
                const val = (i === blanks.length - 1) ? remaining - base * (blanks.length - 1) : base;
                if (val > 0) fill.set(r.key, String(val));
            });
        });
        return fill.size ? current.map(r => (fill.has(r.key) ? { ...r, oocytes: fill.get(r.key) } : r)) : current;
    };
    const validate = (src = list) => {
        const rowErrors = {};
        const setErr = (k, f, m) => { (rowErrors[k] ||= {})[f] = m; };
        const considered = src.filter(r => r.clivageId || !isEmptyNew(r));
        considered.forEach(r => {
            if (!r.aspiration_id) setErr(r.key, 'donor', 'Elegí la donante.');
            const frozen = !!r.clivageId && r.embryosFloor > 0;
            const semen = sameSemen ? globalSemen : r.semen;
            if (!frozen && !semen) setErr(r.key, 'semen', 'Elegí el semen.');
            const ooc = Number(r.oocytes);
            const oocValid = r.oocytes !== '' && Number.isInteger(ooc) && ooc >= 0;
            if (!oocValid) setErr(r.key, 'oocytes', 'Óvocitos inválidos.');
            else if (ooc > r.viables) setErr(r.key, 'oocytes', `Óvocitos > viables (${r.viables}).`);
            // emb = embryos_count (Σ stages incl DG). El tope vs óvocitos solo aplica
            // con óvocitos válidos (si no, ya marcamos 'Óvocitos inválidos' — no duplicar).
            const emb = stagesSum(r.stages);
            if (oocValid && emb > ooc) setErr(r.key, 'embryos', 'Embriones > óvocitos.');
            // piso del backend: embryos_count (incl DG) no baja de COUNT(embriones activos).
            if (r.clivageId && emb < r.embryosFloor) setErr(r.key, 'embryos', `No baja de ${r.embryosFloor}.`);
        });
        // conservation: Σ óvocitos por aspiración ≤ viables (todos los clivages del donante están como filas)
        const byAsp = {};
        considered.forEach(r => { if (r.aspiration_id && Number.isInteger(Number(r.oocytes))) (byAsp[r.aspiration_id] ||= []).push(r); });
        Object.values(byAsp).forEach(group => {
            const sum = group.reduce((a, r) => a + Number(r.oocytes), 0);
            if (group[0].viables != null && sum > group[0].viables) group.forEach(r => setErr(r.key, 'oocytes', `Σ supera viables (${group[0].viables}).`));
        });
        const valid = considered.length > 0 && Object.keys(rowErrors).length === 0 && !!labId && !(sameSemen && !globalSemen);
        return { rowErrors, valid, considered };
    };

    const handleSave = async () => {
        if (!loaded) { toast.error('No se pudieron cargar los clivages'); return; }
        // recalcula los ÓV en blanco (de filas con semen/embriones) antes de validar.
        const rowsToSave = recalcOocytes(list);
        if (rowsToSave !== list) setRows(rowsToSave);
        const { rowErrors, valid, considered } = validate(rowsToSave);
        if (!valid) {
            setErrors(rowErrors);
            toast.error(!labId ? 'Seleccioná el laboratorio' : (sameSemen && !globalSemen) ? 'Seleccioná el semen común' : 'Revisá las filas marcadas');
            return;
        }
        setErrors({});
        setSaving(true);
        const failures = [];
        let attempted = 0;
        const run = async (label, fn) => { attempted++; try { await fn(); } catch (err) { failures.push(err?.message ? `${label} (${err.message})` : label); } };

        // removed existing clivages → DELETE (against the FROZEN snapshot, never the live query)
        const present = new Set(considered.filter(r => r.clivageId).map(r => r.clivageId));
        for (const c of loaded) {
            if (!present.has(c.id)) await run('borrar clivage', () => api.delete(clivagePaths.detail(c.id)));
        }

        for (const r of considered) {
            const emb = stagesSum(r.stages);
            const stages = stagesObj(r.stages);
            if (r.clivageId) {
                const o = r.orig;
                const frozen = r.embryosFloor > 0;
                const semen = sameSemen ? globalSemen : r.semen;
                const labChanged = labDirty && Number(labId) !== o.lab_id;
                const dateChanged = dateDirty && date && date !== o.date;
                const oocChanged = Number(r.oocytes) !== o.oocytes;
                const stagesChanged = JSON.stringify(stages) !== JSON.stringify(stagesObj(o.stages));
                const semenChanged = !frozen && semen && semen.id !== o.semen_id;
                const typeChanged = r.sexedTouched && (r.sexed ? 'sexado' : null) !== (o.type || null);
                if (labChanged || dateChanged || oocChanged || stagesChanged || semenChanged || typeChanged) {
                    const body = { oocytes: Number(r.oocytes), embryos_count: emb, stages };
                    if (labChanged) body.lab_id = Number(labId);
                    if (dateChanged) body.date = date;
                    if (semenChanged) body.semen_id = semen.id;
                    if (r.sexedTouched) body.type = r.sexed ? 'sexado' : null;
                    await run(`clivage ${r.donorName}`, () => api.patch(clivagePaths.detail(r.clivageId), body));
                }
            } else {
                const created = embryosCreated(r.stages); // reales (sin DG)
                const semen = sameSemen ? globalSemen : r.semen;
                const body = {
                    aspiration_id: Number(r.aspiration_id), semen_id: semen.id, lab_id: Number(labId), date: date || today(),
                    oocytes: Number(r.oocytes), embryos_count: emb,
                    ...(emb > 0 ? { stages } : {}),
                    ...(created > 0 ? { preservation: preservationDirective(r.preservation) } : {}),
                    ...(r.sexed ? { type: 'sexado' } : {}),
                };
                await run(`nueva ${r.donorName}`, () => api.post(clivagePaths.list(), body));
            }
        }

        setSaving(false);
        qc.invalidateQueries({ queryKey: clivageKeys.all });
        qc.invalidateQueries({ queryKey: ['opu'] });
        if (attempted > 0 && failures.length === attempted) { toast.error(`No se pudo guardar: ${failures.join(' · ')}`); return; }
        if (failures.length) toast.error(`Guardado con avisos: ${failures.join(' · ')}`);
        else toast.success('Clivages actualizados');
        navigate(`/admin/opu/${sessionId}`);
    };

    const totalEmb = list.reduce((a, r) => a + embryosCreated(r.stages), 0);   // reales (sin DG)
    const totalDisc = list.reduce((a, r) => a + discardedCount(r.stages), 0);  // DG descartados
    const activeCount = list.filter(r => r.clivageId || !isEmptyNew(r)).length;

    return (
        <div className="cl-ws">
            <header className="cl-ws__topbar">
                <div className="cl-ws__brand">
                    <span className="cl-ws__mark"><CowreMark size={30} id="cl-edit-mark" /></span>
                    <span className="cl-ws__brand-text">COWRE</span>
                    <span className="cl-ws__divider" />
                    <span className="cl-ws__title">Editar clivage · {session.code}</span>
                </div>
                <button type="button" className="cl-ws__close" onClick={() => navigate(`/admin/opu/${sessionId}`)} aria-label="Cerrar"><IconClose /></button>
            </header>

            <div className="cl-ws__content">
                <section className="cl-ws__controls">
                    <div className="cl-ws__control">
                        <span className="cl-ws__control-label">Laboratorio</span>
                        <select value={labId} onChange={(e) => { setLabId(e.target.value); setLabDirty(true); }} disabled={saving}>
                            <option value="">Seleccionar…</option>
                            {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                    </div>
                    <div className="cl-ws__control">
                        <span className="cl-ws__control-label">Fecha</span>
                        <input type="date" value={date} max={today()} onChange={(e) => { setDate(e.target.value); setDateDirty(true); }} disabled={saving} />
                    </div>
                    <label className="cl-ws__toggle"><input type="checkbox" checked={sameSemen} onChange={(e) => setSameSemen(e.target.checked)} disabled={saving} /><span>Mismo semen</span></label>
                    <label className="cl-ws__toggle"><input type="checkbox" checked={allSexed} onChange={(e) => toggleAllSexed(e.target.checked)} disabled={saving} /><span>Todo sexado</span></label>
                    {sameSemen && (
                        <div className="cl-ws__control cl-ws__control--semen">
                            <span className="cl-ws__control-label">Semen común (filas sin embriones)</span>
                            <SemenSearch value={globalSemen} onSelect={setGlobalSemen} onClear={() => setGlobalSemen(null)} />
                        </div>
                    )}
                    <button type="button" className="cl-ws__reorg" onClick={() => setShowReorg(true)} disabled={saving || list.length < 2}>Reorganizar</button>
                </section>

                <section className="cl-ws__meta">
                    <span><strong>{session.farm?.name || '—'}</strong></span>
                    <span>· {formatDateOnly(session.date)}</span>
                    <span>· {loaded?.length || 0} clivage(s)</span>
                </section>

                <ClivageEditRows
                    rows={list}
                    errors={errors}
                    donors={donors}
                    sameSemen={sameSemen}
                    onField={setField}
                    onStage={setStage}
                    onSemen={setSemen}
                    onClearSemen={clearSemen}
                    onSexed={setSexed}
                    onRemove={removeRow}
                    onAddClivage={addClivage}
                />
            </div>

            <footer className="cl-ws__footer">
                <span className="cl-ws__footer-hint">{activeCount} clivage(s) · {totalEmb} embrión(es){totalDisc > 0 ? ` · ${totalDisc} DG descartado(s)` : ''}</span>
                <div className="cl-ws__actions">
                    <button type="button" className="cl-ws__cancel" onClick={() => navigate(`/admin/opu/${sessionId}`)} disabled={saving}>Cancelar</button>
                    <button type="button" className="cl-ws__primary" onClick={handleSave} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
                </div>
            </footer>

            {showReorg && (
                <ReorganizeModal
                    items={[...new Map(list.map(r => [String(r.aspiration_id), { id: r.aspiration_id, label: `${r.donorName || 'Donadora'}${r.donorNumber ? ` · ${r.donorNumber}` : ''}` }])).values()]}
                    onApply={applyOrder}
                    onClose={() => setShowReorg(false)}
                />
            )}
        </div>
    );
}
